import React from 'react';
import { ToastContainer } from 'react-toastify';
import { User, X, Phone, Calendar, Globe, UserCheck } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';
import { useUpdateProfile } from '../../hooks/useUserProfile';
import styles from './EditProfileModal.module.css';

const EditProfileModal = ({ onClose, userData }) => {
  const { updateProfile, isLoading } = useUpdateProfile();
  
  const [formData, setFormData] = React.useState({
    phone: userData?.phone || '',
    gender: userData?.gender || '',
    dob: userData?.dob ? new Date(userData.dob).toISOString().split('T')[0] : '',
    language: userData?.language || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await updateProfile(formData);
      onClose();
    } catch (error) {
      // Error handling is managed by the hook
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalIcon}>
            <User size={24} />
          </div>
          <h2>Edit Profile</h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.inputGroup}>
            <label>Full Name</label>
            <input
              type="text"
              value={userData?.name || ''}
              className={`${styles.input} ${styles.disabledInput}`}
              disabled
              placeholder="Name cannot be changed"
            />
            <small className={styles.disabledText}>Name cannot be edited</small>
          </div>

          <div className={styles.inputGroup}>
            <label>Email Address</label>
            <input
              type="email"
              value={userData?.email || ''}
              className={`${styles.input} ${styles.disabledInput}`}
              disabled
              placeholder="Email cannot be changed"
            />
            <small className={styles.disabledText}>Email cannot be edited</small>
          </div>
          
          <div className={styles.inputGroup}>
            <label>Phone Number</label>
            <div className={styles.inputWrapper}>
              <Phone size={20} className={styles.inputIcon} />
              <input
                type="tel"
                name="phone"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={handleInputChange}
                className={styles.input}
                disabled={isLoading}
                maxLength="10"
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label>Gender</label>
            <div className={styles.inputWrapper}>
              <UserCheck size={20} className={styles.inputIcon} />
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={styles.input}
                disabled={isLoading}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label>Date of Birth</label>
            <div className={styles.inputWrapper}>
              <Calendar size={20} className={styles.inputIcon} />
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className={styles.input}
                disabled={isLoading}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label>Preferred Language</label>
            <div className={styles.inputWrapper}>
              <Globe size={20} className={styles.inputIcon} />
              <select
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                className={styles.input}
                disabled={isLoading}
              >
                <option value="">Select Language</option>
                <option value="hindi">Hindi</option>
                <option value="english">English</option>
                <option value="punjabi">Punjabi</option>
                <option value="gujarati">Gujarati</option>
                <option value="marathi">Marathi</option>
                <option value="bengali">Bengali</option>
                <option value="tamil">Tamil</option>
                <option value="telugu">Telugu</option>
                <option value="kannada">Kannada</option>
                <option value="malayalam">Malayalam</option>
                <option value="urdu">Urdu</option>
                <option value="sanskrit">Sanskrit</option>
              </select>
            </div>
          </div>
          
          <div className={styles.modalButtons}>
            <button 
              type="button" 
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.submitBtn}
              disabled={isLoading}
            >
              {isLoading ? 'Updating Profile...' : 'Update Profile'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default EditProfileModal; 